import React, { useState, useEffect } from 'react';
import { Loader2, Play, CheckCircle, AlertTriangle, HelpCircle } from 'lucide-react';
import apiClient from '../../lib/apiClient';
import { ACQ_TYPES, TIMEFRAMES, fmtDate } from './constants';
import useDepartments from '../../hooks/useDepartments';
import './ScrapeTriggerForm.css';

function ScrapeTriggerForm({ onTrigger, loading, selectedDepartment }) {
  const today = new Date();
  const { departments } = useDepartments();
  const [jobType, setJobType] = useState('scprs');
  const [dateFrom, setDateFrom] = useState(fmtDate(new Date(today.getFullYear() - 1, today.getMonth(), today.getDate())));
  const [dateTo, setDateTo] = useState(fmtDate(today));
  const [dept, setDept] = useState('');
  const [acqType, setAcqType] = useState('IT Goods');
  const [supplierId, setSupplierId] = useState('');
  const [activePreset, setActivePreset] = useState('1Y');
  const [caleprocureCodes, setCaleprocureCodes] = useState({});

  useEffect(() => {
    apiClient.get('/api/completeness/caleprocure-codes')
      .then(data => {
        // Build map: caleprocure_code -> verification record
        const map = {};
        (data.codes || []).forEach(c => { map[c.caleprocure_code] = c; });
        setCaleprocureCodes(map);
      })
      .catch(() => {});
  }, []);

  // Default to selected department when it changes or departments load.
  // Prefer department_id (canonical int PK) for matching because bu_code and
  // acronym can both be duplicated across merged + active department rows —
  // matching by text picks the first hit, which may be the inactive stub.
  useEffect(() => {
    if (selectedDepartment && departments.length > 0) {
      const selectedId = selectedDepartment.department_id;
      const buCode = selectedDepartment.department_number || selectedDepartment.department_code || selectedDepartment.code;
      const match =
        (selectedId != null && departments.find(d => d.department_id === selectedId)) ||
        departments.find(d => d.code === buCode || d.code === String(buCode)) ||
        departments.find(d => d.acronym === selectedDepartment.acronym);
      if (match) {
        setDept(match.code);
      }
    }
  }, [selectedDepartment, departments]);

  // Code verification status for selected dept
  const codeInfo = dept ? caleprocureCodes[dept] : null;
  const codeStatus = codeInfo
    ? codeInfo.has_it_goods === true ? 'verified'
    : codeInfo.has_it_goods === false ? 'no-it'
    : 'unverified'
    : 'unknown';

  const applyPreset = (tf) => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - tf.days);
    setDateFrom(fmtDate(from));
    setDateTo(fmtDate(to));
    setActivePreset(tf.label);
  };

  const handleDateChange = () => setActivePreset(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (jobType === 'canary') {
      onTrigger({ type: 'canary', _label: 'Canary Test' });
      return;
    }
    if (jobType === 'fiscal') {
      onTrigger({ type: 'fiscal', supplierId, dateFrom, dateTo, _label: `Fiscal · ${supplierId}` });
      return;
    }
    const selected = departments.find(d => d.code === dept);
    onTrigger({
      type: 'scprs',
      dateFrom,
      dateTo,
      departmentCode: dept || undefined,
      acquisitionType: acqType || undefined,
      _label: selected ? `${selected.acronym} · ${acqType}` : `All Depts · ${acqType}`,
    });
  };

  const handleSurvey = (e) => {
    e.preventDefault();
    if (!dept) { alert('Select a department to survey.'); return; }
    const selected = departments.find(d => d.code === dept);
    onTrigger({
      type: 'survey',
      dateFrom,
      dateTo,
      departmentCode: dept,
      deptAcronym: selected?.acronym,
      acquisitionType: acqType || undefined,
      _label: `Survey: ${selected?.acronym || dept} · ${dateFrom}–${dateTo}`,
    });
  };

  return (
    <form className="scrape-form" onSubmit={handleSubmit}>
      {/* Source selector */}
      <div className="scrape-form__source">
        {[
          { id: 'scprs', label: 'SCPRS' },
          { id: 'fiscal', label: 'Fiscal' },
          { id: 'canary', label: 'Test' },
        ].map(jt => (
          <button
            key={jt.id}
            type="button"
            className={`scrape-form__source-btn ${jobType === jt.id ? 'is-active' : ''}`}
            onClick={() => setJobType(jt.id)}
          >
            {jt.label}
          </button>
        ))}
      </div>

      {jobType === 'canary' ? (
        <p className="scrape-form__hint">Quick connectivity test</p>
      ) : (
        <div className="scrape-form__controls">
          {/* Timeframe presets */}
          <div className="scrape-form__presets">
            {TIMEFRAMES.map(tf => (
              <button
                key={tf.label}
                type="button"
                className={`scrape-form__preset ${activePreset === tf.label ? 'is-active' : ''}`}
                onClick={() => applyPreset(tf)}
              >
                {tf.label}
              </button>
            ))}
          </div>

          {/* Date range */}
          <div className="scrape-form__dates">
            <input
              type="date"
              value={dateFrom}
              onChange={e => { setDateFrom(e.target.value); handleDateChange(); }}
              required
            />
            <span>&ndash;</span>
            <input
              type="date"
              value={dateTo}
              onChange={e => { setDateTo(e.target.value); handleDateChange(); }}
              required
            />
          </div>

          {/* Filters */}
          {jobType === 'fiscal' ? (
            <input
              type="text"
              className="scrape-form__supplier"
              value={supplierId}
              onChange={e => setSupplierId(e.target.value)}
              placeholder="Supplier ID"
              required
            />
          ) : (
            <div className="scrape-form__filters">
              <div className="scrape-form__dept-wrap">
                <select value={dept} onChange={e => setDept(e.target.value)}>
                  <option value="">All Depts</option>
                  {departments.map(d => {
                    const ci = caleprocureCodes[d.code];
                    const verified = ci?.has_it_goods === true;
                    const noIt = ci?.has_it_goods === false;
                    return (
                      <option key={d.code} value={d.code}>
                        {d.acronym || d.code} ({d.code}){verified ? ' ✓' : noIt ? ' ✗' : ''}
                      </option>
                    );
                  })}
                </select>
                {dept && codeStatus !== 'unknown' && (
                  <div className={`scrape-form__code-badge scrape-form__code-badge--${codeStatus}`}>
                    {codeStatus === 'verified' && (
                      <>
                        <CheckCircle size={11} />
                        <span>
                          Code {dept} verified — {codeInfo.it_goods_count?.toLocaleString()} IT Goods POs
                          {codeInfo.last_verified_at && ` (last ${new Date(codeInfo.last_verified_at).toLocaleDateString()})`}
                        </span>
                      </>
                    )}
                    {codeStatus === 'no-it' && (
                      <>
                        <AlertTriangle size={11} />
                        <span>Code {dept} has no IT data on record — verify this is the correct CaleProcure BU code</span>
                      </>
                    )}
                    {codeStatus === 'unverified' && (
                      <>
                        <HelpCircle size={11} />
                        <span>Code {dept} not yet verified — {codeInfo?.notes || 'run a scrape to confirm IT data exists'}</span>
                      </>
                    )}
                  </div>
                )}
              </div>
              <select value={acqType} onChange={e => setAcqType(e.target.value)}>
                <option value="">All Types</option>
                {ACQ_TYPES.map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}

      {/* Submit */}
      <button type="submit" className="scrape-form__submit" disabled={loading}>
        {loading ? (
          <><Loader2 size={14} className="sd-spin" /> Starting&hellip;</>
        ) : (
          <><Play size={14} /> Run</>
        )}
      </button>
    </form>
  );
}

export default ScrapeTriggerForm;
